import { JobRequest } from "./JobRequest";
import { JobRequestStatus } from "./JobRequestStatus";

/** How many attempts the server gives a job before it stays Failed. */
export const JOB_MAX_ATTEMPTS = 5;

/** The server will not run the job again, so taking it off the list changes nothing it did. */
export const isJobFinished = (job: JobRequest): boolean =>
  job.status === JobRequestStatus.Completed ||
  job.status === JobRequestStatus.Stopped ||
  (job.status === JobRequestStatus.Failed && (job.retryCount ?? 0) >= JOB_MAX_ATTEMPTS);

/** A Failed job the server will try again. */
const isWaitingForRetry = (job: JobRequest): boolean =>
  job.status === JobRequestStatus.Failed && (job.retryCount ?? 0) < JOB_MAX_ATTEMPTS;

/** A stop was asked for and the job has not ended yet. */
export const isJobStopping = (job: JobRequest): boolean =>
  !!job.stopRequestedAt && (job.status === JobRequestStatus.Processing || isWaitingForRetry(job));

/** Stop applies to a stoppable job that is running or waiting for its next attempt, and is not stopping already. */
export const canStopJob = (job: JobRequest): boolean =>
  !!job.canStop &&
  !job.stopRequestedAt &&
  (job.status === JobRequestStatus.Processing || isWaitingForRetry(job));

/**
 * Abort (before it starts) or Remove (once it has ended). A running job offers neither, and a stoppable job waiting
 * for a retry is stopped instead: the server refuses to remove it, since stopping is what publishes its report and
 * shows the entities it hid again.
 */
export const canAbortJob = (job: JobRequest): boolean =>
  job.status !== JobRequestStatus.Processing && !(job.canStop && isWaitingForRetry(job));
